<template>
  <main id="content" aria-labelledby="main-title" v-if="page">
    
    <ElementHeroInfo :content="{
      title: 'Webinars'
    }" />

    <Section
    margins="reset-top"
    v-if="upcomingWebinars.length > 0">
      <FadeIn delay="200">
        <Container>
          <h2 class="text-h3 mb-8">Upcoming webinars</h2>
          <div class="mx-auto mt-0 grid grid-cols-1 gap-x-8 gap-y-16 lg:mx-0 lg:max-w-none lg:grid-cols-3"> 
            <ElementWebinarCard 
            v-for="(article, index) in upcomingWebinars" 
            :key="'webinar-thumb-'+index" 
            :article="article" 
            type="webinar"
            />
          </div>
        </Container>
      </FadeIn>
    </Section>

    <Section v-if="pastWebinars" :margins="upcomingWebinars.length < 1 ? 'reset-top' : ''" >
      <FadeIn delay="200">
        <Container>
          <h2 class="text-h3 mb-8">Past webinars</h2>
          <div class="mx-auto mt-0 grid grid-cols-1 gap-x-8 gap-y-16 lg:mx-0 lg:max-w-none lg:grid-cols-3"> 
            <ElementWebinarCard 
            v-for="(article, index) in pastWebinars" 
            :key="'past-webinar-thumb-'+index" 
            :article="article" 
            type="webinar"
            />
          </div>
        </Container>
      </FadeIn>
    </Section>

    <ElementBookDemoCta />

  </main>
</template>

<script setup>
const route = useRoute()
const { getItems } = useCms()

const currentLocale = computed(() => {
  return route.path.startsWith('/nz') ? 'nz' : 'au'
})

const sydneyDate = ref(
  new Date().toLocaleString('en-AU', {
    timeZone: 'Australia/Sydney', 
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).split('/').reverse().join('-')
)

const today = new Date(sydneyDate.value)

const { data } = useAsyncData(
  `${currentLocale.value}-webinar-page`,
  async () => {
    
    const pageData = await getItems({
      collection: 'webinars',
      params: {
        fields: [
          '*',
          'seo.*',
          'seo.meta_title', 
          'seo.meta_description',
          'seo.meta_image.*'
        ]
      }
    })
    
    const articlesData = await getItems({
      collection: 'webinar',
      params: {
        filter: {
          status: { _eq: 'published' },
          locale: { _contains: currentLocale.value }
        },
        fields: [
          '*', 'image.id', 'image.filename_download', 'image.description', 'image.width', 'image.height', 'category.category_id.title', 'category.category_id.slug',
        ],
        sort: "-date"
      }
    })    

    return {
      page: pageData,
      articles: articlesData
    }
  }
)

const page = computed(() => data?.value?.page)
const articles = computed(() => data?.value?.articles)

const pastWebinars = computed(() => {
  if (!articles.value || !Array.isArray(articles.value)) return []
  return articles.value.filter(item => {
    const webinarDate = new Date(item.date)
    return today > webinarDate
  })
})

const upcomingWebinars = computed(() => {
  if (!articles.value || !Array.isArray(articles.value)) return []
  return articles.value.filter(item => {
    const webinarDate = new Date(item.date)
    return today <= webinarDate
  }).sort((a, b) => new Date(a.date) - new Date(b.date))
})

const seo = computed(() => {
  if (page.value?.seo) {
    return useSeo(page.value.seo, route.fullPath)
  }
  return {}
})

useHead(seo)

</script>